/**
 * @internal true
 * @owner security
 * @description Página de gestão de aprovações de computadores pendentes (Master / Diretor).
 */
import { useState, useEffect } from 'react';
import { ShieldCheck, ShieldAlert, Loader2, RefreshCw, Clock, MapPin, Globe } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import '../../components/ui/datatable.css';

interface SolicitacaoRow {
  id: string;
  solicitanteId: string;
  criadoEm: string;
  expiraEm: string;
  contexto?: {
    ip?: string;
    userAgent?: string;
    navegador?: string;
    sistemaOp?: string;
    solicitanteNome?: string;
    solicitantePerfil?: string;
    solicitanteCargo?: string;
    lat?: number;
    lng?: number;
  };
  solicitante: {
    nome: string;
    email: string;
    perfil: string;
    cargo?: string | null;
  };
}

export function AprovacoesPage() {
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchSolicitacoes = async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      const res = await api.get<SolicitacaoRow[]>('/auth/security/approval/solicitacoes-superiores');
      setSolicitacoes(res.data || []);
    } catch (err: any) {
      console.error('[Aprovacoes] Erro ao carregar solicitações:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitacoes(true);
    // Polling a cada 5 segundos
    const interval = setInterval(() => fetchSolicitacoes(false), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResponder = async (solicitacaoId: string, resposta: 'AUTORIZADO' | 'NEGADO') => {
    setActionLoading(solicitacaoId);
    try {
      await api.post('/auth/security/approval/responder-superior', {
        solicitacaoId,
        resposta,
      });
      toast.success(resposta === 'AUTORIZADO' ? 'Acesso autorizado!' : 'Solicitação rejeitada.');
      // Remove localmente para resposta visual imediata
      setSolicitacoes(prev => prev.filter(s => s.id !== solicitacaoId));
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Falha ao responder solicitação.';
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const cleanBrowserName = (ua?: string) => {
    if (!ua) return 'Navegador Desconhecido';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return ua.length > 25 ? ua.substring(0, 25) + '...' : ua;
  };

  return (
    <div className="admin-page" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-main)' }}>
            <ShieldCheck className="text-primary" size={24} style={{ color: '#1B365D' }} />
            Solicitações de Acesso Pendentes
          </h1>
          <p className="text-sm text-muted-foreground" style={{ color: 'var(--text-muted)' }}>
            Aprove ou rejeite solicitações de acesso temporário a computadores feitas por professores e funcionários.
          </p>
        </div>
        <button
          onClick={() => fetchSolicitacoes(true)}
          className="btn-cancel flex items-center gap-2"
          disabled={isLoading}
          style={{ padding: '8px 14px' }}
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      <div className="data-table-wrapper flex-1 overflow-hidden" style={{ minHeight: 300, display: 'flex', flexDirection: 'column', background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12 }}>
        <div className="dt-container flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-20" style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Loader2 size={32} className="animate-spin text-primary" style={{ color: '#1B365D' }} />
            </div>
          ) : solicitacoes.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-3" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12 }}>
              <ShieldCheck size={48} className="text-emerald-500" style={{ color: '#10b981' }} />
              <p className="font-medium text-sm" style={{ fontWeight: 700, margin: 0 }}>Nenhuma solicitação pendente.</p>
              <p className="text-xs" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Novos pedidos de liberação de computador aparecerão aqui em tempo real.</p>
            </div>
          ) : (
            <table className="dt-table w-full text-left" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>Solicitante</th>
                  <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>Dispositivo / Detalhes</th>
                  <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>Expiração</th>
                  <th style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color, #e5e7eb)', textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {solicitacoes.map((sol) => (
                  <tr key={sol.id} className="dt-row" style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div className="font-bold text-sm text-foreground" style={{ fontWeight: 700, fontSize: '0.85rem' }}>{sol.solicitante.nome}</div>
                      <div className="text-xs text-muted-foreground" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{sol.solicitante.email}</div>
                      <div className="text-xs text-muted-foreground mt-0.5" style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        <strong>ID:</strong> <code style={{ fontSize: '0.65rem', background: 'rgba(255,255,255,0.05)', padding: '2px 4px', borderRadius: 4 }}>{sol.solicitanteId}</code>
                      </div>
                      <div className="mt-1" style={{ marginTop: 4 }}>
                        <span 
                          style={{ 
                            fontSize: '0.68rem', 
                            fontWeight: 700, 
                            padding: '2px 6px', 
                            borderRadius: 4, 
                            background: sol.solicitante.perfil === 'PROFESSOR' ? '#eff6ff' : '#f1f5f9',
                            color: sol.solicitante.perfil === 'PROFESSOR' ? '#2563eb' : '#475569'
                          }}
                        >
                          {sol.solicitante.perfil} {sol.solicitante.cargo ? `· ${sol.solicitante.cargo}` : ''}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '0.78rem' }}>
                      <div>
                        <strong>OS:</strong> {sol.contexto?.sistemaOp || 'Desconhecido'} | <strong>Navegador:</strong> {cleanBrowserName(sol.contexto?.navegador)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5" style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        <strong>IP:</strong> {sol.contexto?.ip || 'Desconhecido'}
                      </div>
                      {sol.contexto?.lat && sol.contexto?.lng && (
                        <div className="text-xs text-muted-foreground mt-1" style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={12} className="text-[#a855f7]" />
                          <strong>Local:</strong>{' '}
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${sol.contexto.lat},${sol.contexto.lng}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#6c5ce7', textDecoration: 'underline', fontWeight: 600 }}
                          >
                            Ver no Mapa ({sol.contexto.lat.toFixed(4)}, {sol.contexto.lng.toFixed(4)})
                          </a>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <CountdownDisplay expiraEm={sol.expiraEm} onExpired={() => fetchSolicitacoes(false)} />
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleResponder(sol.id, 'NEGADO')}
                          disabled={actionLoading !== null}
                          className="btn-cancel"
                          style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        >
                          Rejeitar
                        </button>
                        <button
                          onClick={() => handleResponder(sol.id, 'AUTORIZADO')}
                          disabled={actionLoading !== null}
                          className="btn-save"
                          style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                        >
                          {actionLoading === sol.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            'Aprovar (2 horas)'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CountdownDisplay({ expiraEm, onExpired }: { expiraEm: string; onExpired: () => void }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const calcTimeLeft = () => {
      const diff = new Date(expiraEm).getTime() - Date.now();
      if (diff <= 0) {
        onExpired();
        return 0;
      }
      return Math.floor(diff / 1000);
    };

    setTimeLeft(calcTimeLeft());
    const timer = setInterval(() => {
      const left = calcTimeLeft();
      if (left <= 0) {
        clearInterval(timer);
      }
      setTimeLeft(left);
    }, 1000);

    return () => clearInterval(timer);
  }, [expiraEm, onExpired]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  if (timeLeft <= 0) {
    return <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ef4444' }}>Expirado</span>;
  }

  return (
    <span 
      style={{ 
        fontSize: '0.75rem', 
        fontWeight: 700, 
        color: timeLeft <= 60 ? '#ef4444' : '#d97706',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4
      }}
    >
      <Clock size={12} />
      {minutes}m {seconds}s
    </span>
  );
}
